import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { css } from '@emotion/core';
import { useMsgText } from '../../../localization/hooks';
import { Str } from '../../../localization/translationKeys';
import ModalHeader from '../../../layout/modal/ModalHeader';
import LocalDiv from '../../../localization/LocalDiv';
import Modal from '../../../layout/modal/Modal';
import { FLEX_COLUMN } from '../../../layout/flexBox';
import { useHistory } from 'react-router-dom';
import { useAppState } from '../../../core/redux/hooks';
import { isNilOrEmpty, doNothing } from '../../../language/module';
import { useProjIds } from '../../../navigation/hooks';
import EmailDevPlanForm from './EmailDevPlanForm';
import { sendDevPlanAction } from '../devPlanAction/api/sendDevPlanAction';
import { useDispatch } from 'react-redux';
import { useAddCollaboratorModalProps, useDevPlanCollaborationSetting } from '../collaboration/hooks';
import ConfirmationModal from '../../../layout/modal/ConfirmationModal';


const styles = {
  modal:    [
    FLEX_COLUMN,
    css`
      min-height: 0;
      height: auto;
    `,
  ],
  root:     [
    FLEX_COLUMN,
    css`

    `,
  ],
  formRoot: css`
    background-color: transparent;
  `,
};


type Props = {
  className?: string;
};


/**
 * EmailDevPlanModal: a modal component that renders a modal with the email form
 */
const EmailDevPlanModal: React.FC<Props> = props => {
  const history = useHistory();

  const dispatch = useDispatch();

  const projIds = useProjIds();

  const emailRequired   = useAppState(state => !!state.projectSummary?.developmentPlanEmailLevelId);
  const requiredLevelId = useAppState(state => state.projectSummary?.developmentPlanEmailLevelId);
  const raterMap        = useAppState(state => state.projectRaters?.[`${projIds.projId}`] ?? []);

  const raters = useMemo(
    () => Object.values(raterMap).filter(rater => rater.levelId === requiredLevelId && !isNilOrEmpty(rater.email)),
    [raterMap, requiredLevelId],
  );

  const dismiss = useCallback(() => history.goBack(), [history]);

  const collaborationEnabled = useDevPlanCollaborationSetting();

  const title = useMsgText(collaborationEnabled ? Str.SharePlan : Str.EmailPlan);
  const sendEmailBtnMsg = useMemo(() => collaborationEnabled ? Str.InviteShare : Str.Send, [collaborationEnabled]);

  const sendDevPlan = useCallback(
    (email: string, comment?: string) => {
      dispatch(sendDevPlanAction(projIds.projId, projIds.participantId, email, comment));
    },
    [dispatch, projIds.participantId, projIds.projId],
  );

  const project = useAppState(state => state.projectSummary);
  const devPlanProjectId =  project?.devPlanProjectId ?? projIds.projId;

  const {
          addCollaboratorModalVisible,
          showAddCollaboratorModal,
          dismissAddCollaboratorModal,
          confirmAddCollaboratorMsg,
          onConfirmAddCollaborator,
        } = useAddCollaboratorModalProps(devPlanProjectId, projIds.participantId, dispatch);

  const onClickSendEmailBtn = useCallback(collaborationEnabled ? showAddCollaboratorModal : sendDevPlan, []);

  const addCollaborator = useCallback(
    () => {
      onConfirmAddCollaborator();
      dismiss();
    },
    [dismiss, onConfirmAddCollaborator],
  );

  const cancelAddCollaborator = useCallback(
    () => {
      dismissAddCollaboratorModal();
      dismiss();
    },
    [dismiss, dismissAddCollaboratorModal],
    );

  return (
      <Modal onDismiss={dismiss} dismissable={true} css={styles.modal} className={props.className}>
        <ModalHeader title={title} onDismiss={dismiss} />
        <LocalDiv css={styles.root}>
          <EmailDevPlanForm
            participantId={projIds.participantId}
            projectId={projIds.projId}
            emailingRequired={emailRequired}
            raters={raters}
            onSent={collaborationEnabled ? doNothing : dismiss}
            onClickSubmitBtn={onClickSendEmailBtn}
            submitBtnMsg={sendEmailBtnMsg}
            css={styles.formRoot}
          />
        </LocalDiv>
        {
          addCollaboratorModalVisible && (
            <ConfirmationModal
              confirmationTxt={confirmAddCollaboratorMsg}
              primaryActionStr={Str.Yes}
              secondaryActionStr={Str.No}
              onSelectPrimary={addCollaborator}
              onSelectSecondary={cancelAddCollaborator}
              onDismiss={dismissAddCollaboratorModal}
            />
          )
        }
      </Modal>
    );
};

export default EmailDevPlanModal;
